export class Label {
  public root: any = null;

  public pivot: any = null;

  public label;

  private options: any;

  private context: any;

  constructor(options, context) {
    this.options = options;
    this.context = context;
    this.initRoot();
    this.initLabel();
  }

  protected initRoot() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.pivot = new THREE.Object3D();
    this.root.add(this.pivot);
  }

  initLabel() {
    this.label = this.makeLabel(this.options.text, {
      fontsize: 48,
      width: 400,
      textColor: {
        r: 255,
        g: 255,
        b: 255,
        a: 1.0,
      },
      borderColor: {
        r: 0,
        g: 0,
        b: 0,
        a: 0,
      },
      backgroundColor: {
        r: 0,
        g: 0,
        b: 0,
        a: 0.7,
      },
      borderThickness: 0,
      borderRadius: 16,
      ...this.options,
    });
    this.pivot.add(this.label);
  }

  wrapText = (
    context: CanvasRenderingContext2D,
    text: string,
    _x: number,
    y: number,
    maxWidth: number,
    lineHeight: number,
  ) => {
    const words = text.split(' ');
    let line = '';
    const lines: { text: string; y: number }[] = [];
    let maxY = 0;

    for (let n = 0; n < words.length; n += 1) {
      const testLine = `${line + words[n]} `;
      const metrics = context.measureText(testLine);
      const testWidth = metrics.width;

      if (testWidth > maxWidth) {
        y += lineHeight;
        lines.push({ text: line, y });
        line = `${words[n]} `;
      } else {
        line = testLine;
      }
    }
    y += lineHeight;
    lines.push({ text: line, y });
    maxY = y + lineHeight / 2;
    return { lines, maxY };
  };

  protected makeLabel(message, parameters) {
    const THREE = this.context.three;
    if (parameters === undefined) parameters = {};

    const fontface = Object.getOwnPropertyDescriptor(parameters, 'fontface')
      ? parameters.fontface
      : 'Arial';
    const fontsize = Object.getOwnPropertyDescriptor(parameters, 'fontsize')
      ? parameters.fontsize
      : 18;
    const width = Object.getOwnPropertyDescriptor(parameters, 'width') ? parameters.width : 200;
    const borderThickness = Object.getOwnPropertyDescriptor(parameters, 'borderThickness')
      ? parameters.borderThickness
      : 4;
    const borderColor = Object.getOwnPropertyDescriptor(parameters, 'borderColor')
      ? parameters.borderColor
      : {
          r: 0,
          g: 0,
          b: 0,
          a: 1.0,
        };
    const backgroundColor = Object.getOwnPropertyDescriptor(parameters, 'backgroundColor')
      ? parameters.backgroundColor
      : {
          r: 255,
          g: 255,
          b: 255,
          a: 1.0,
        };
    const textColor = Object.getOwnPropertyDescriptor(parameters, 'textColor')
      ? parameters.textColor
      : {
          r: 255,
          g: 255,
          b: 255,
          a: 1.0,
        };
    const borderRadius = Object.getOwnPropertyDescriptor(parameters, 'borderRadius')
      ? parameters.borderRadius
      : 8;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      console.error('Could not get context');
      return;
    }

    context.font = `Bold ${fontsize}px ${fontface}`;
    context.fillStyle = `rgba(${backgroundColor.r},${backgroundColor.g},${backgroundColor.b},${backgroundColor.a})`;
    context.strokeStyle = `rgba(${borderColor.r},${borderColor.g},${borderColor.b},${borderColor.a})`;

    const lineHeight = fontsize;
    const result = this.wrapText(context, message, 0, 0, width, lineHeight);

    const rectW = width;
    const rectH = result.maxY;

    canvas.width = width;
    canvas.height = rectH;

    context.font = `Bold ${fontsize}px ${fontface}`;
    context.fillStyle = `rgba(${backgroundColor.r},${backgroundColor.g},${backgroundColor.b},${backgroundColor.a})`;
    context.strokeStyle = `rgba(${borderColor.r},${borderColor.g},${borderColor.b},${borderColor.a})`;

    context.lineWidth = borderThickness;

    this.roundRect(context, borderThickness / 2, borderThickness / 2, rectW, rectH, borderRadius);

    context.fillStyle = `rgba(${textColor.r}, ${textColor.g}, ${textColor.b}, 1.0)`;

    result.lines.forEach((line) => {
      context.fillText(line.text, 0, line.y);
    });

    context.fillStyle = `rgba(${textColor.r}, ${textColor.g}, ${textColor.b}, 1.0)`;
    const label = canvas;

    const texture = new THREE.CanvasTexture(label);
    const planeWidth = label.width / 1000; // scaling down
    const planeHeight = label.height / 1000;

    return this.makePlane(planeWidth, planeHeight, texture);
  }

  private makePlane(width, height, texture) {
    const THREE = this.context.three;
    const geometry = new THREE.PlaneGeometry(width, height, 10, 10);

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.DoubleSide,
      blending: THREE.NormalBlending,
      transparent: true,
    });
    return new THREE.Mesh(geometry, material);
  }

  // function for drawing rounded rectangles
  // eslint-disable-next-line class-methods-use-this
  private roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
}
